import * as yup from 'yup';
import { Eye, EyeOff, Trash2 } from 'react-feather';
import { FormManager, useForm } from 'react-form-state-manager';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetProductQuery as GetProduct,
  ProductType, SaveProductPricingInput, SavePromotionInput,
} from '__generated__/graphql';

import { ErrorBag, getServerErrors } from '../../../../common/helpers';
import { RestrictedToTicketsForm } from '../../../Common/PricingForm';
import { translationsForAttribute } from '../../../Translations/helpers';
import DateRangeForm, { dateRangeObject } from '../../../Common/DateRangeForm';
import ProductInvoiceOptionsForm from './ProductInvoiceOptionsForm';
import UI from '../../../../common/UI';
import useCurrencyInput from '../../../../common/useCurrencyInput';
import useEditProductMutation from './useEditProductMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Event = GetProduct['event'];
type Product = GetProduct['product'];

const getEditProductPricingSchema = (isDonation: boolean) => yup.object().shape({
  vat_percentage: yup.number().min(0).max(99.999).nullable(),
  promotions: yup.array(yup.object().shape({
    title: yup.string().nullable().test('required', '', (value, { path }) => (
      isDonation || (path === 'promotions[0].title' ? true : !!value)
    )),
    capacity: yup.number().nullable(),
    description: yup.string().ensure(),
    amount: yup.number().test('required', '', (value) => (
      !isDonation || value >= 0
    )),
    restricted_to_tickets: yup.array(yup.object().shape({
      id: yup.string(),
    })),
    restricted_to_promotions: yup.array(yup.object().shape({
      id: yup.string(),
    })),
    ...dateRangeObject(),
  })),
});

const getValues = (product: Product) => ({
  promotions: [
    ...product.promotions.map((promotion) => ({
      id: promotion.id,
      title: promotion.title,
      title_translations: translationsForAttribute(promotion.translations, 'title'),
      capacity: promotion.capacity,
      amount: promotion.amount,
      start_date: promotion.start_date,
      end_date: promotion.end_date,
      active: promotion.active,
      restricted_to_tickets: promotion.restricted_to_tickets.map(({ id }) => ({ id })),
      restricted_to_promotions: promotion.restricted_to_promotions.map(({ id }) => ({ id })),
    })),
    ...(product.type === ProductType.donation ? [{ active: true }] : []),
  ],
  required: product.required,
  vat_percentage: product.vat_percentage,
  invoice_text: product.invoice_text,
  invoice_text_translations: translationsForAttribute(product.translations, 'invoice_text'),
  hide_on_invoice: product.hide_on_invoice,
  ledger_account: product.ledger_account,
});

export interface EditProductPricingFormProps {
  product: Product;
  event: Event;
}

const EditProductPricingDonationForm = ({ product, event }: EditProductPricingFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const form = useForm<SaveProductPricingInput>({
    values: getValues(product),
  });

  useNavigationPrompt(form.changed());

  const isDonation = product && product.type === ProductType.donation;

  const getPromotionData = () => {
    const promotions = (form.values.promotions.length === 1
      // Since the promotion capacity field disappears when there is only 1 promotion,
      // make sure we unset it on save.
      ? [{ ...form.values.promotions[0], capacity: null, title: null, active: true }]
      : form.values.promotions);

    if (isDonation) {
      return promotions.slice(0, promotions.length - 1).map((promotion) => ({
        ...promotion,
        restricted_to_tickets: promotions[0].restricted_to_tickets,
        restricted_to_promotions: promotions[0].restricted_to_promotions,
      }));
    }

    return promotions;
  };

  const formValues = {
    ...form.values,
    promotions: getPromotionData(),
  };

  const [editProduct, { error, loading }] = useEditProductMutation({
    input: {
      id: product.id,
      pricing: formValues,
    },
    onSuccess: (product) => {
      form.reset(getValues(product));
    },
  });

  const editProductPricingSchema = useMemo(
    () => getEditProductPricingSchema(isDonation),
    [isDonation],
  );

  const { valid, errors } = useValidation({
    schema: editProductPricingSchema,
    values: formValues,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editProduct()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ block: true, mb: 1 }}>
            {t('pricing')}
          </UI.Legend>
          <UI.InputDescription>
            {t('product_pricing_description')}
          </UI.InputDescription>
        </UI.Div>

        <DonationPricingForm
          form={form}
          event={event}
          product={product}
          errors={errors}
        />

        <UI.InputGroup>
          <UI.InputLabel>{t('required_donation')}</UI.InputLabel>
          <UI.InputDescription>
            {t('required_donation_description')}
          </UI.InputDescription>
          <UI.Checkbox {...form.checkbox('required')}>
            {t('donation_is_required')}
          </UI.Checkbox>
        </UI.InputGroup>

        <UI.HR />

        <ProductInvoiceOptionsForm
          form={form}
          event={event}
          errors={errors}
          withoutVat
        />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_product')}
          closeTo={`/${project.id}/events/${event.id}/products`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface ProductPricingFormProps {
  form: FormManager<SaveProductPricingInput>;
  event: Event;
  product?: Product;
  errors: ErrorBag;
}

const DonationPricingForm = ({ form, event, product, errors }: ProductPricingFormProps) => {
  const { t } = useTranslation();
  const currencyInput = useCurrencyInput();

  const standardPromotionIndex = 0;

  const lastPromotion: any = form.values.promotions[form.values.promotions.length - 1];
  const promotionKeys = useRef(form.values.promotions.map((value, index) => index));

  useEffect(() => {
    if (typeof lastPromotion.amount !== 'undefined' && lastPromotion.amount !== null) {
      form.set('promotions', (promotions: SavePromotionInput[]) => [
        ...promotions,
        { active: true },
      ]);
      promotionKeys.current.push(Math.max(0, ...promotionKeys.current) + 1);
    }
  }, [lastPromotion.amount]);

  const deletePromotion = (index: number) => {
    form.delete('promotions', index);
    promotionKeys.current.splice(index, 1);
  };

  const togglePromotion = (index: number) => {
    form.set(`promotions.${index}.active`, (active: boolean) => !active);
  };

  const hasRestrictions = form.values.promotions[0].restricted_to_tickets?.length > 0;
  const [showRestrictions, setShowRestrictions] = useState(hasRestrictions);
  const toggleRestrictions = () => {
    form.set('promotions.0.restricted_to_tickets', []);
    form.set('promotions.0.restricted_to_promotions', []);
    setShowRestrictions(!showRestrictions);
  };

  return (
    <UI.FormGrid>
      <UI.FormGrid sc={{ gutter: 0.5 }}>
        <UI.Div>
          <UI.InputLabel htmlFor="PromotionAmount_1">
            {t('example_amounts')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('donation_examples_description')}
          </UI.InputDescription>
        </UI.Div>
        {form.values.promotions.map((value, index) => {
          const promotion = product?.promotions.filter(({ id }) => id === value.id)[0];

          return index !== standardPromotionIndex && (
            <UI.FormGrid
              sc={{ columns: '1fr 1fr' }}
              key={value.id || promotionKeys.current[index]}
            >
              <UI.InputGroup
                sc={{
                  valid: !errors[`promotions.${index}.amount`], touched: form.getTouched(`promotions.${index}.amount`),
                }}
              >
                <UI.CurrencyInput
                  {...form.text(`promotions.${index}.amount`, {
                    inputHandler: {
                      parse: currencyInput.parse,
                      format: currencyInput.value,
                    },
                  })}
                  sc={{ color: !value.active ? 'gray.400' : undefined }}
                  placeholder={t('example_amount')}
                  id="PromotionAmount"
                />
                <UI.ErrorMessages
                  attribute={t('amount')}
                  errors={errors[`promotions.${index}.amount`]}
                />
              </UI.InputGroup>
              <UI.Div sc={{ pt: 1 }}>
                {index + 1 < form.values.promotions.length && (
                  <>
                    <UI.Button
                      sc={{ blank: true, size: 'sm', color: 'gray.400' }}
                      onClick={() => togglePromotion(index)}
                    >
                      <UI.Icon>
                        {value.active ? <Eye /> : <EyeOff />}
                      </UI.Icon>
                    </UI.Button>
                    {' '}
                    <UI.Button
                      sc={{ blank: true, size: 'sm', color: 'gray.400', mr: 1 }}
                      onClick={() => deletePromotion(index)}
                    >
                      <UI.Icon>
                        <Trash2 />
                      </UI.Icon>
                    </UI.Button>
                    {' '}
                    <UI.Small>
                      <UI.Strong>
                        {promotion?.sold_count || 0}
                        &times;
                      </UI.Strong>
                      {' '}
                      {t('chosen')}
                    </UI.Small>
                  </>
                )}
              </UI.Div>
            </UI.FormGrid>
          );
        })}
      </UI.FormGrid>

      <DateRangeForm
        form={form}
        errors={errors}
        keys={{
          startDate: `promotions.${standardPromotionIndex}.start_date`,
          startTime: `promotions.${standardPromotionIndex}.start_time`,
          endDate: `promotions.${standardPromotionIndex}.end_date`,
          endTime: `promotions.${standardPromotionIndex}.end_time`,
        }}
        labels={{
          startDate: t('start_of_sales'),
          endDate: t('end_of_sales'),
        }}
      />

      <UI.InputGroup>
        <UI.Checkbox
          onChange={() => toggleRestrictions()}
          checked={showRestrictions}
        >
          {t('restrict_product_to_specific_tickets')}
        </UI.Checkbox>
      </UI.InputGroup>

      {showRestrictions && (
        <RestrictedToTicketsForm
          form={form}
          index={0}
          errors={errors}
          tickets={event.tickets}
        />
      )}

    </UI.FormGrid>
  );
};

export default EditProductPricingDonationForm;
